/** A screen's own row of links to its sibling screens - **the second level of tabs, never a third**
 * (`docs/design/v5-build-scope.md`).
 *
 * Settings has five tabs; "You" is one of them and holds three screens (profile, sign-in and
 * notifications, your data requests), and Organisation holds two (companies and sites, financials).
 * Those are different URLs, so this is a `nav` of links with `aria-current` on the one you are on,
 * for the reason `SectionTabs` gives: the ARIA tab pattern promises panels that swap in place, and
 * a `role="tab"` that navigates breaks that promise for a screen reader.
 */

import { Link } from "@tanstack/react-router";

import { keepSearch } from "../screens/search";
import type { ScreenSearch } from "../screens/search";
import styles from "./PageLinks.module.css";

export interface PageLink {
  to: string;
  label: string;
}

export function PageLinks({
  label,
  links,
  current,
  search,
  testId,
}: {
  /** What the row is, for a screen reader: "Your settings". */
  label: string;
  links: readonly PageLink[];
  /** The path of the link that is the screen being read. */
  current: string;
  /** The reader's current search, so the window and the basis travel with them. */
  search: Partial<ScreenSearch>;
  testId?: string;
}) {
  return (
    <nav className={styles.row} aria-label={label} data-testid={testId ?? "page-links"}>
      {links.map((link) => {
        const on = link.to === current;
        return (
          <Link
            key={link.to}
            to={link.to}
            search={keepSearch(search)}
            className={styles.link}
            data-active={on}
            aria-current={on ? "page" : undefined}
          >
            {link.label}
          </Link>
        );
      })}
    </nav>
  );
}
